import { cn } from "../../lib/utils";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { buildCongressDateLabel } from "../../domains/congress/congressDateLabel";
import { resolveCongressCountdownCopy } from "../../domains/congress/congressCountdownCopy";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { useLocationState } from "../../domains/app/hooks/useLocationState";
import { resolveCongressNames } from "../../domains/congress/congressName";
import { deriveCongressCountdown } from "../../domains/congress/deriveCongressCountdown";
import {
  deriveCongressPhase,
  deriveCongressPresentation,
} from "../../../../api/src/shared/domain/congress-lifecycle";
import {
  gaEventCongressSpaceViewed,
  type CongressAnalyticsContext,
} from "../../tools/analytics/domains/congress.analytics";
import { useApiQuery, type Api } from "../../services/api";
import { CongressCard } from "../ui/CongressCard";
import { CongressSpaceContent } from "./CongressSpaceContent";
import { DEFAULT_COVER_COLOR } from "../../domains/congress/coverColor";

// Stamped by whoever navigated here (Discovery banner, room hero, chip). Absent
// on a cold entry — an external deep link or a plain visit to the tab.
const locationStateSchema = z.object({
  congressEntryPoint: z
    .enum(["banner", "room_block", "chip"])
    .nullable()
    .default(null),
});

type PdfCardBlock = Extract<
  Api["get"]["/webapp/private/congress-spaces/:id/content"]["output"]["body"][number],
  { kind: "pdfCard" }
>;

export function RoomCongressResources({
  roomId,
  visible,
  scope,
  now,
  layout,
}: {
  roomId: string;
  /** Hidden when a room-section (non-congress) filter is active */
  visible: boolean;
  /**
   * Which spaces this renders:
   * - `chip`: the one space of the active congress chip;
   * - `placed`: one space, at its place in the "All" list;
   * - `unplaced`: every space the "All" list does not place, above it. None
   *   in the normal case; a cached list, or an older API after a rollback,
   *   places none, and the blocks then sit above the list as they used to.
   */
  scope:
    | { kind: "chip"; spaceId: string }
    | { kind: "placed"; spaceId: string }
    | { kind: "unplaced"; placedSpaceIds: readonly string[] };
  /**
   * Owned by the page, not ticked here: the chip row above decides the same
   * congress/section presentation from the same instant, and two independent
   * minute tickers could disagree for a minute at the window boundary.
   */
  now: Date;
  /**
   * `phone`: the mobile Resources tab, inset and one column. `desktop`: the
   * congress chip of the desktop Resources tab (Figma
   * "D3.explore.room.resources.congress"), the column's own width, the space's
   * cards on two columns, and a PDF that opens in the tab's own viewer.
   */
  layout:
    | { kind: "phone" }
    | {
        kind: "desktop";
        onOpenDocument: (block: PdfCardBlock) => void;
      };
}) {
  // ONE binding on purpose: `contentLang` scopes the payload AND every string
  // drawn from it. `activeLang` is gone from this file — the section's own chrome
  // is translated implicitly by `t()`, which is the interface locale by
  // definition. See the axis rule in `useActiveLang`.
  const { contentLang } = useActiveLang();
  const { t } = useTranslation();
  const countdownCopy = resolveCongressCountdownCopy({ contentLang, t });
  const { congressEntryPoint } = useLocationState(locationStateSchema);
  // `contentLang` scopes every congress payload (top sessions, PDF documents,
  // attached publications) — same axis as the room sections below.
  const result = useApiQuery("/webapp/private/rooms/:id/congress-spaces", {
    param: { id: roomId },
    query: { language: contentLang },
  });

  const spaces =
    !visible || result.state !== "success"
      ? []
      : result.body.filter((space) => {
          switch (scope.kind) {
            case "chip":
            case "placed":
              return space.id === scope.spaceId;
            case "unplaced":
              return !scope.placedSpaceIds.includes(space.id);
            default:
              throw new Error(scope satisfies never);
          }
        });

  // A space is "visited" once it is actually drawn — under a congress chip it
  // is the whole page, with no filter it is one block in the room's list.
  useEffectOnce(() => {
    for (const space of spaces) {
      gaEventCongressSpaceViewed({
        context: {
          spaceId: space.id,
          congressName: resolveCongressNames(
            space.translations,
            space.fallbackName,
            contentLang,
            space.city,
            space.country
          ).name,
          phase: deriveCongressPhase(space, now),
          roomId,
        },
        entryPoint:
          congressEntryPoint !== null
            ? congressEntryPoint
            : scope.kind === "chip"
              ? "deep_link"
              : "resources_tab",
      });
    }
  }, [spaces.map((space) => space.id).join(",")]);

  if (spaces.length === 0) return null;

  return (
    <div
      className={cn(
        "flex flex-col gap-4",
        layout.kind === "phone" ? "px-5 py-2" : null
      )}
    >
      {spaces.map((space) => {
        const { name, shortName, description, city, country } =
          resolveCongressNames(
            space.translations,
            space.fallbackName,
            contentLang,
            space.city,
            space.country
          );
        const countdown = deriveCongressCountdown(space, now);
        // Same render pass, same `now`, and the phase it rests on is the phase
        // stamped on `analytics` five lines below — so the look of the space
        // and what the events say about it cannot drift apart.
        const presentation = deriveCongressPresentation(space, now);
        const coverColor =
          space.coverColor === null ? DEFAULT_COVER_COLOR : space.coverColor;
        const analytics: CongressAnalyticsContext = {
          spaceId: space.id,
          congressName: name,
          phase: deriveCongressPhase(space, now),
          roomId,
        };
        return (
          <div key={space.id} className="flex flex-col gap-3">
            {/*
              Past the grace window the congress chrome goes and the CONTENT
              STAYS. The card and the description leave together: a description
              left behind reads as a stray sentence above an unheaded list, and
              it is written to introduce a congress, not a resources section.

              `CongressSpaceContent` below is deliberately outside this: same
              space id, same blocks, same attribution. Demoting the look must
              not empty the "after the congress" column of the board, which is
              the number the whole space is measured by once it is over.
            */}
            {presentation === "congress" && (
              <CongressCard
                display={{ variant: "min" }}
                name={shortName}
                dateLabel={buildCongressDateLabel(
                  {
                    startDate: space.startDate,
                    endDate: space.endDate,
                    city,
                    country,
                  },
                  {
                    contentLang,
                    monthStyle: "short",
                    placeSeparator: " - ",
                  }
                )}
                coverColor={coverColor}
                coverTextMode={space.coverTextMode}
                countdown={countdown}
                countdownCopy={countdownCopy}
              />
            )}
            {presentation === "congress" && description !== "" && (
              <p className="m-0 font-dm-sans text-sm/5 tracking-tight text-ds-slate-500">
                {description}
              </p>
            )}
            {(() => {
              const content = (
                <CongressSpaceContent
                  spaceId={space.id}
                  analytics={analytics}
                  language={contentLang}
                  roomId={roomId}
                  accentColor={coverColor}
                  shortName={shortName}
                  curatorName={space.curator.name}
                  curatorRole={space.curator.role}
                  curatorAvatarUrl={space.curator.avatarUrl}
                  topSessionsCount={space.topSessionsCount}
                  topSessionsCardTitle={space.topSessionsCardTitle}
                  surface={
                    layout.kind === "phone"
                      ? { kind: "phone" }
                      : {
                          kind: "desktop-tab",
                          onOpenDocument: layout.onOpenDocument,
                        }
                  }
                />
              );
              return layout.kind === "phone" ? (
                content
              ) : (
                <div className="mt-5 grid grid-cols-2 gap-6">{content}</div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
}
